import React, { useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import get from 'lodash/get';
import { ApolloQueryResult } from '@apollo/client';
import { PaymentMethodId, PaymentMethodPanel } from '@atlassian/commerce-payment-methods';
import {
  ContactsTooltip,
  ContactsTooltipType,
  FormattedAddress,
  SummaryPanelContainer,
  SummaryPanelIcon,
  SummaryPanelRow,
  useFeatureToggle,
  AchPanel,
} from '@css-xp/base';
import {
  PaymentMethodType,
  PaymentOnTerms,
  FetchInvoiceGroupDetailsPpcQuery,
  CommerceAddress,
  SoldToAddress,
  Ach,
} from '@css-xp/graphtypes/src/generated-types-new';
import { STATSIG_FEATURE_GATES, routePaths } from '@css-xp/ppc-constants';
import { getRouteUrl, isPaypalPaymentMethod, isAchPaymentMethod } from '@css-xp/ppc-utils';
import { useApplicationContext } from '@css-xp/base/common-context';
import TermPaymentMethod from '@css-xp/base/term-payment-method/term-payment-method';
import { getTaxIdLabels } from '@css-xp/ppc-tax-component';
import { NoData, SoldToAddressDetailsLink, SoldToAddress as SoldToAddressDetails } from '@css-xp/ppc-common';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { EndCustomerContacts } from '@css-xp/contact-resources';
import { PaypalPaymentMethod } from '@css-xp/ppc-paypal';
import './billing-payment-details-section.scss';

export const BillingPaymentDetailsSection = ({
  invoiceGroupDetails,
  shouldShowACHPaymentMethod,
}: {
  invoiceGroupDetails: ApolloQueryResult<FetchInvoiceGroupDetailsPpcQuery | undefined>;
  shouldShowACHPaymentMethod?: boolean;
}) => {
  const { txaId } = useParams<{ txaId: string }>();
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1);
  const data = useApplicationContext();
  const selectedPartnerAccountData = data?.partnerAccounts?.find(
    ({ transactionAccountId }: { transactionAccountId: string }) => transactionAccountId === txaId
  );

  const getDefaultPaymentSection = useCallback(() => {
    if (invoiceGroupDetails.loading) {
      return renderSkeletonLoader();
    }
    const defaultPaymentMethod = invoiceGroupDetails.data?.invoiceGroup?.defaultPaymentMethod;

    if (defaultPaymentMethod?.type === PaymentMethodType.DEFERRED) {
      const termPaymentStatus = invoiceGroupDetails.data?.invoiceGroup?.termPaymentStatus?.status;
      const invoiceGroupPaymentMethod = invoiceGroupDetails.data?.invoiceGroup?.defaultPaymentMethod as PaymentOnTerms;
      const paymentMethod = invoiceGroupPaymentMethod?.paymentMethod;
      const totalCreditLimit = invoiceGroupPaymentMethod?.totalCreditLimit;
      const hasPayOnStatement = invoiceGroupPaymentMethod?.terms?.payOnStatement;
      const invoiceDueDays = invoiceGroupPaymentMethod?.terms?.invoiceDueDays;

      const termPaymentType = invoiceDueDays ? `NET${invoiceDueDays}` : paymentMethod;

      return (
        <TermPaymentMethod
          paymentNetType={hasPayOnStatement ? '' : (termPaymentType as string)}
          totalLimit={hasPayOnStatement ? undefined : (totalCreditLimit as number)}
          currentTermAccountStatus={termPaymentStatus}
          hasPayOnAccount={!!hasPayOnStatement}
        />
      );
    } else if (isPaypalPaymentMethod(defaultPaymentMethod?.type)) {
      const emailID = (defaultPaymentMethod as any)?.payPal?.email;
      return (
        <div className="payment-method">
          <PaypalPaymentMethod emailID={emailID} />
        </div>
      );
    } else if (shouldShowACHPaymentMethod && isAchPaymentMethod(defaultPaymentMethod?.type)) {
      const achPaymentMethod = defaultPaymentMethod as Ach;
      return (
        <AchPanel
          bankName={achPaymentMethod?.ach?.bankName}
          last4={achPaymentMethod?.ach?.last4}
          showInfoInline={false}
          showBorder
          width="20%"
        />
      );
    } else if (defaultPaymentMethod) {
      const expMonth = +get(defaultPaymentMethod, 'card.expirationMonth', 0);
      const expYear = +get(defaultPaymentMethod, 'card.expirationYear', 0);
      const card = {
        brand: get(defaultPaymentMethod, 'card.brand', ''),
        expMonth,
        expYear,
        last4: get(defaultPaymentMethod, 'card.last4Numbers', ''),
        name: get(defaultPaymentMethod, 'card.cardHolderName', ''),
      };
      return (
        <div className="payment-method">
          <PaymentMethodPanel
            paymentMethod={{
              id: defaultPaymentMethod.id as PaymentMethodId,
              type: defaultPaymentMethod.type,
              ...(defaultPaymentMethod?.type === PaymentMethodType.CARD && {
                card,
              }),
              default: defaultPaymentMethod?.default ?? false,
            }}
          />
        </div>
      );
    }
    return 'No payment method associated with this account';
  }, [invoiceGroupDetails]);

  const renderSkeletonLoader = () => {
    return (
      <SkeletonLoaderWrapper>
        <SkeletonLoader width="200px" marginBottom="0" />
      </SkeletonLoaderWrapper>
    );
  };
  const getBillingProfile = () => {
    if (invoiceGroupDetails.loading) {
      return renderSkeletonLoader();
    }
    return (
      <Link
        to={`${getRouteUrl(routePaths.billingProfileDetails, {
          txaId: txaId,
          billingProfileId: `${get(invoiceGroupDetails, 'data.invoiceGroup.id', '')}`,
        })}`}
      >
        {get(invoiceGroupDetails, 'data.invoiceGroup.name', '')}
      </Link>
    );
  };

  const getCompanyName = () => {
    if (invoiceGroupDetails.loading) {
      return renderSkeletonLoader();
    } else if (get(invoiceGroupDetails, 'data.invoiceGroup.shipToParty.name', '')) {
      return (
        <SoldToAddressDetailsLink
          txaId={txaId}
          companyName={get(invoiceGroupDetails, 'data.invoiceGroup.shipToParty.name', '')}
        >
          {get(invoiceGroupDetails, 'data.invoiceGroup.shipToParty.name', '')}
        </SoldToAddressDetailsLink>
      );
    }
    return <NoData />;
  };

  const getCustomerContacts = () => {
    if (invoiceGroupDetails.loading) {
      return renderSkeletonLoader();
    }
    return (
      <EndCustomerContacts
        invoiceGroupId={get(invoiceGroupDetails, 'data.invoiceGroup.id', '')}
        transactionAccountId={txaId}
      />
    );
  };

  const getSoldToAddressSection = () => {
    const shipToParty = get(invoiceGroupDetails, 'data.invoiceGroup.shipToParty', null);
    if (invoiceGroupDetails.loading) {
      return renderSkeletonLoader();
    }
    return (
      <SoldToAddressDetails
        shipTo={shipToParty}
        txaId={txaId}
        hasLineBreak={!isDesignIteration1Enabled}
        shouldDisableLink={isDesignIteration1Enabled}
      />
    );
  };

  const getBillingAddressSection = () => (
    <div>
      <FormattedAddress
        addressLine1={get(selectedPartnerAccountData, 'billTo.street', '')}
        city={get(selectedPartnerAccountData, 'billTo.city', '')}
        country={get(selectedPartnerAccountData, 'billTo.country', '')}
        postcode={get(selectedPartnerAccountData, 'billTo.postalCode', '')}
        state={get(selectedPartnerAccountData, 'billTo.state', '')}
      />
    </div>
  );

  const getTaxInformationSection = (address: CommerceAddress | SoldToAddress | null | undefined) => {
    const taxIdLabels = getTaxIdLabels(address);

    return taxIdLabels?.map((taxIdLabel, idx) => (
      <SummaryPanelRow
        key={`tax-id-label-${idx}`}
        label={taxIdLabel.label}
        value={taxIdLabel.value}
        isHiddenByDefault
        useWrapper={isDesignIteration1Enabled}
      />
    ));
  };

  return (
    <SummaryPanelContainer
      title={isDesignIteration1Enabled ? 'Address and payment details' : 'Customer and payment details'}
      iconKey={SummaryPanelIcon.BILLING_PAYMENT_ICON}
      version={isDesignIteration1Enabled ? 2 : undefined}
    >
      {isEnabled(STATSIG_FEATURE_GATES.ENABLE_BILLING_PROFILE_LINK) && (
        <SummaryPanelRow label="Billing Profile" value={getBillingProfile()} useWrapper={isDesignIteration1Enabled} />
      )}
      <SummaryPanelRow
        label="Payment method"
        value={getDefaultPaymentSection()}
        useWrapper={isDesignIteration1Enabled}
      />
      <SummaryPanelRow label="Company" value={getCompanyName()} useWrapper={isDesignIteration1Enabled} />
      <SummaryPanelRow
        label="Sold-to address"
        value={getSoldToAddressSection()}
        useWrapper={isDesignIteration1Enabled}
      />
      {getTaxInformationSection(invoiceGroupDetails?.data?.invoiceGroup?.shipToParty)}
      <SummaryPanelRow
        label="Billing address"
        value={getBillingAddressSection()}
        isHiddenByDefault
        useWrapper={isDesignIteration1Enabled}
      />
      {getTaxInformationSection(selectedPartnerAccountData)}
      <SummaryPanelRow
        label={
          <ContactsTooltip type={ContactsTooltipType.CUSTOMER} iconSize="small">
            Customer contacts
          </ContactsTooltip>
        }
        value={getCustomerContacts()}
        isHiddenByDefault
        useWrapper={isDesignIteration1Enabled}
      />
    </SummaryPanelContainer>
  );
};
